import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, useToast } from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { useTrade } from '@/lib/trade';
import { SCHEDULE_KEY, scheduleApi } from '@/features/schedule/schedule-api';

const NONE = '__none__';
const teamSchema = z.array(
  z.object({ id: z.string(), name: z.string(), isActive: z.boolean().optional(), role: z.string().optional() }),
);

/**
 * שיבוץ ידני מכרטיס העבודה. אזהרות (חופף, בחופש, מחוץ לשעות) מוצגות — לא
 * חוסמות: הבעלים יודע מה הוא עושה.
 */
export function AssigneePicker({ taskId, assignedToUserId }: { taskId: string; assignedToUserId: string | null }) {
  const { t } = useTranslation('tasks');
  const toast = useToast();
  const qc = useQueryClient();
  const trade = useTrade();
  const team = useQuery({ queryKey: ['team', 'assignees'], queryFn: ({ signal }) => request('/team', { schema: teamSchema, signal }) });

  const move = useMutation({
    mutationFn: (userId: string | null) => scheduleApi.move(taskId, { assignedToUserId: userId }),
    onSuccess: async ({ warnings }) => {
      if (warnings.length) toast.info(t('assignee.saved'), t('assignee.conflicts', { count: warnings.length }));
      else toast.success(t('assignee.updated'));
      await qc.invalidateQueries({ queryKey: ['task', taskId] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      void qc.invalidateQueries({ queryKey: SCHEDULE_KEY });
    },
    onError: (e) => toast.error(t('assignee.failed'), e instanceof ApiError ? e.message : undefined),
  });

  // צפייה בלבד לא יוצא לעבודות — השרת גם דוחה שיבוץ כזה.
  const members = (team.data ?? []).filter((m) => m.isActive !== false && m.role !== 'VIEWER');
  return (
    <Select
      value={assignedToUserId ?? NONE}
      disabled={move.isPending || team.isPending}
      onValueChange={(v) => move.mutate(v === NONE ? null : v)}
    >
      <SelectTrigger className="h-8 w-40 text-xs" aria-label={t('assignee.aria', { worker: trade.worker.one })}>
        <SelectValue placeholder={t('assignee.none')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{t('assignee.none')}</SelectItem>
        {members.map((m) => (
          <SelectItem key={m.id} value={m.id}>
            {m.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
